import React, { useState } from 'react';
import { Chip, Collapse, Grid, Paper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TableBarOutlinedIcon from '@mui/icons-material/TableBarOutlined';
import { useDraggable } from '@dnd-kit/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  AccountData,
  AddonOptionData,
  AddonStatus,
  AddonUIObjectData,
  BookingType,
  OrderData,
  OrderService,
} from '../../../api';
import { WalkInChip } from '../../../common/WalkInChip';
import { isProjectSelectionPending } from '../../../common/ProjectSelectionPendingBadge';
import { isPendingHoldExpired, PENDING_ORDER_HATCH_ON_LIGHT } from '../../../common/pendingOrderUtils';
import { customerCommentText } from '../../../common/OrderUtils';
import { whatsAppLinkFormatter } from '../../../common/whatsAppLinkFormatter';
import { useSafeExecute } from '../../../common/apiNotifications/safeExecute';
import ConfirmationDialog from '../../../common/ui/ConfimationDialog';
import { getOrderPages } from '../../../utils/AccountUtils';
import { priceFormatterFromCurrency } from '../../../utils/utils';
import CheckinDialog from '../../dialogs/CheckinDialog/CheckinDialog';
import { canFinishNow } from '../../dialogs/EditOrderDialog/components/dateAndTimePanel/finishNow';
import { useOrders } from '../../swr/useOrders';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { useAreasView } from '../AreasViewContext';
import { CARD_FILL } from '../areasPalette';
import { DEFAULT_ORDER_PAGE_COLOR, mb, softFill } from '../../../common/theme/mbTokens';
import { roomColorOf } from '../../roomColors';
import { RoomChip } from './RoomChip';
import {
  BODY_WEIGHT,
  CARD_PADDING,
  CARD_PADDING_INLINE,
  CARD_RADIUS,
  FONT_SIZE,
  ICON_SIZE,
  MUTED_TEXT,
} from '../agendaStyles';
import { formatSeconds } from '../utils/areasFormat';
import { AgendaCardState, elapsedMinutesSinceCheckin } from '../utils/occupancyMath';
import { OrderPlacement, OrderPlacementType } from '../utils/orderAreas';
import { seatedTableCount } from '../../utils/orderSeating';
import { buildReleaseTableRequest } from '../utils/releaseTable';
import { AgendaOrderActions } from './AgendaOrderActions';
import { AgendaOrderDetails } from './AgendaOrderDetails';
import { SettledOrderLine } from './SettledOrderLine';

type AgendaOrderCardProps = {
  order: OrderData;
  state: AgendaCardState;
  placement: OrderPlacement;
  account: AccountData;
  language: 'he' | 'en';
  timeFormat: string;
  isToday: boolean;
  nowSeconds: number;
  isNextUp: boolean;
  lateMinutes: number | null;
  isNew: boolean;
  isHighlighted: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isDragEnabled: boolean;
};

const MAX_CHIPS_ON_SMALL_SCREEN = 2;

/**
 * One order of the day. Two dense rows (who and when; where, how many, how to reach them) with the
 * actions beside them on desktop and under them on a phone. Tapping the rows opens the details.
 */
export const AgendaOrderCard = ({
  order,
  state,
  placement,
  account,
  language,
  timeFormat,
  isToday,
  nowSeconds,
  isNextUp,
  lateMinutes,
  isNew,
  isHighlighted,
  isExpanded,
  onToggleExpanded,
  isDragEnabled,
}: AgendaOrderCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { startDate, endDate } = useCalendar();
  const { updateItem: updateOrder } = useOrders({ startDate, endDate });
  const { accountToday } = useAreasView();
  const safeExecute = useSafeExecute();
  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [isReleaseConfirmOpen, setIsReleaseConfirmOpen] = useState(false);
  const isDraggable = isDragEnabled && placement.type !== OrderPlacementType.NON_SCHEDULER;
  const {
    setNodeRef: setDragNodeRef,
    listeners: dragListeners,
    isDragging,
  } = useDraggable({ id: order.id, disabled: !isDraggable });

  const amountDue = Math.max(0, order.balance_due ?? 0);
  const extraItems = order.extra_items ?? [];
  const startSeconds = order.time ?? 0;
  const canReleaseTable =
    order.date === accountToday && canFinishNow(startSeconds, startSeconds + order.duration, nowSeconds);

  const releaseTable = async (isConfirmed: boolean) => {
    if (!isConfirmed) {
      setIsReleaseConfirmOpen(false);
      return;
    }
    try {
      const releasedOrder = await safeExecute({
        apiCall: () => OrderService.editOrder(buildReleaseTableRequest(order, nowSeconds)),
        successMessage: t('calendar.areas_view.table_released'),
      });
      await updateOrder(releasedOrder);
      setIsReleaseConfirmOpen(false);
    } catch {
      // safeExecute already surfaced the toast; the dialog stays open for a retry.
    }
  };

  const name = [order.firstname, order.lastname].filter(Boolean).join(' ');
  const orderPage = getOrderPages(account, true).find((candidate) => candidate.id === order.order_page_id);
  const addonIcons = (orderPage?.objects || [])
    .filter(
      (object): object is AddonUIObjectData =>
        object.type === 'addon' && (object as AddonUIObjectData).status !== AddonStatus.ARCHIVED,
    )
    .map((addon) =>
      addon.options.find(
        (option) => option.id === (order.custom_details as Record<string, any> | undefined)?.[addon.id],
      ),
    )
    .filter((option): option is AddonOptionData => !!option?.icon)
    .map((option) => ({ id: option.id, icon: option.icon as string, label: option.label[language] }));
  const staffComment = order.comment?.trim() || undefined;
  const customerComment = customerCommentText(order, orderPage);
  const noteTooltip = (
    <Grid container direction="column" gap={0.5}>
      {staffComment && (
        <>
          <Typography variant="caption">{t('calendar.areas_view.staff_comment')}</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: FONT_SIZE.secondary }}>{staffComment}</Typography>
        </>
      )}
      {customerComment && (
        <>
          <Typography variant="caption">{t('calendar.areas_view.customer_comment')}</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: FONT_SIZE.secondary }}>{customerComment}</Typography>
        </>
      )}
    </Grid>
  );
  const timeRange = `${formatSeconds(startSeconds, timeFormat)}–${formatSeconds(
    startSeconds + order.duration,
    timeFormat,
  )}`;
  const formatAmount = (amount: number) =>
    priceFormatterFromCurrency(amount, account.currency, undefined, false, false);

  if (state === AgendaCardState.DONE && !isExpanded) {
    return (
      <SettledOrderLine
        order={order}
        name={name}
        timeRange={timeRange}
        account={account}
        language={language}
        formatAmount={formatAmount}
        onToggleExpanded={onToggleExpanded}
      />
    );
  }

  const lineChips: { key: string; node: React.ReactNode }[] = [
    ...(isNextUp
      ? [
          {
            key: 'next-up',
            node: (
              <Chip
                size="small"
                label={t('calendar.areas_view.next_up')}
                sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 600 }}
              />
            ),
          },
        ]
      : []),
    ...(isNew
      ? [
          {
            key: 'new',
            node: (
              <Chip
                size="small"
                label={t('calendar.areas_view.new_today')}
                sx={{ bgcolor: 'mb.claySoft', color: 'mb.clayInk', fontWeight: 600, fontSize: 12, height: 22 }}
              />
            ),
          },
        ]
      : []),
    ...(order.booking_type === BookingType.WALK_IN ? [{ key: 'walk-in', node: <WalkInChip /> }] : []),
  ];
  const visibleChips = isSmallScreen ? lineChips.slice(0, MAX_CHIPS_ON_SMALL_SCREEN) : lineChips;
  const overflowChips = isSmallScreen ? lineChips.slice(MAX_CHIPS_ON_SMALL_SCREEN) : [];

  const workshopColor = orderPage?.color || DEFAULT_ORDER_PAGE_COLOR;
  const workshopLabel = orderPage?.label?.[language] || orderPage?.label?.he || '';

  const roomChips: { key: string; label: string; dot: string; soft: string; textColor?: string }[] = (() => {
    if (placement.type === OrderPlacementType.AREAS) {
      return placement.areas.map((area) => ({
        key: `${area.configId}-${area.areaId}`,
        label: area.label,
        ...roomColorOf(account, area.configId, area.areaId),
      }));
    }
    if (placement.type === OrderPlacementType.UNASSIGNED) {
      return [
        {
          key: 'unassigned',
          label: t('calendar.areas_view.no_table'),
          dot: mb.clayInk,
          soft: mb.claySoft,
          textColor: 'mb.clayInk',
        },
      ];
    }
    // A non-scheduler order has no place of its own: the workshop chip already says where it is.
    if (placement.type === OrderPlacementType.NON_SCHEDULER) {
      return [];
    }
    return [
      {
        key: 'off-grid',
        label: t('calendar.areas_view.off_grid_chip'),
        dot: workshopColor,
        soft: softFill(workshopColor),
      },
    ];
  })();

  // How many tables, never which ones: the number is what staff scan for; the drawer has the labels.
  const tableCount =
    placement.type === OrderPlacementType.AREAS
      ? placement.areas.reduce((total, area) => total + area.tablesInArea, 0)
      : seatedTableCount(order);

  const elapsedMinutes = isToday ? elapsedMinutesSinceCheckin(order, moment()) : null;
  const elapsedText =
    elapsedMinutes === null
      ? null
      : elapsedMinutes >= 60
      ? t('calendar.areas_view.elapsed_hm', { hours: Math.floor(elapsedMinutes / 60), minutes: elapsedMinutes % 60 })
      : t('calendar.areas_view.elapsed_m', { minutes: elapsedMinutes });

  const timeElement = (
    <Typography
      dir="ltr"
      sx={{
        fontSize: FONT_SIZE.time,
        fontWeight: 400,
        color: MUTED_TEXT,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
      }}>
      {timeRange}
    </Typography>
  );
  const lateElement =
    lateMinutes !== null ? (
      <Typography
        dir="ltr"
        sx={{
          fontSize: FONT_SIZE.detail,
          fontWeight: 700,
          color: 'error.main',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
        {t('calendar.areas_view.late_by', { minutes: lateMinutes })}
      </Typography>
    ) : null;
  // The invoice/company name rides on the name line; hidden when it only repeats the customer's name.
  const companyName =
    order.alternative_full_name?.trim() &&
    order.alternative_full_name.trim().toLowerCase() !== name.trim().toLowerCase()
      ? order.alternative_full_name.trim()
      : undefined;
  // The name is what staff scan for: it wraps to two lines instead of truncating and never gives way to the chips.
  const nameElement = (
    <Typography
      sx={{
        fontSize: FONT_SIZE.title,
        fontWeight: 700,
        lineHeight: 1.2,
        flex: isSmallScreen ? 1 : '1 1 160px',
        minWidth: isSmallScreen ? 0 : '120px',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}>
      {name || t('calendar.areas_view.unnamed_order')}
      {companyName && (
        <Tooltip title={t('calendar.areas_view.invoice_name')}>
          <Typography
            component="span"
            sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, color: MUTED_TEXT, marginInlineStart: 1 }}>
            {companyName}
          </Typography>
        </Tooltip>
      )}
    </Typography>
  );
  const chipsElement = visibleChips.map((chip) => <React.Fragment key={chip.key}>{chip.node}</React.Fragment>);
  const addonElements = addonIcons.map(({ id, icon, label }) => (
    <Tooltip key={id} title={label}>
      <span style={{ fontSize: 16, lineHeight: 1 }}>{icon}</span>
    </Tooltip>
  ));
  const orderNumberElement =
    order.order_number !== undefined ? (
      <Typography
        dir="ltr"
        sx={{
          fontSize: FONT_SIZE.detail,
          fontWeight: BODY_WEIGHT,
          color: MUTED_TEXT,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
        #{order.order_number}
      </Typography>
    ) : null;

  const rows = (
    <Grid
      container
      direction="column"
      gap={1.25}
      wrap="nowrap"
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={onToggleExpanded}
      onKeyDown={(event: React.KeyboardEvent) => {
        // Enter on the phone link must activate the link, not fold the card.
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggleExpanded();
        }
      }}
      sx={{ flex: 1, minWidth: 0, cursor: 'pointer', padding: CARD_PADDING }}>
      {isSmallScreen ? (
        <Grid container direction="column" gap={0.5} wrap="nowrap">
          <Grid container alignItems="flex-start" gap={1.5} wrap="nowrap">
            {nameElement}
            {orderNumberElement}
          </Grid>
          <Grid container alignItems="center" gap={0.75} wrap="wrap">
            {timeElement}
            {lateElement}
            {chipsElement}
            {addonElements}
          </Grid>
        </Grid>
      ) : (
        <Grid container alignItems="center" columnGap={1.5} rowGap={0.5} wrap="wrap">
          {timeElement}
          {lateElement}
          {nameElement}
          {chipsElement}
          {addonElements}
          {orderNumberElement}
        </Grid>
      )}

      <Grid container alignItems="center" gap={2} rowGap={1} wrap="wrap">
        <Grid container alignItems="center" gap={0.75} wrap="wrap" sx={{ width: 'auto', minWidth: 0 }}>
          {workshopLabel && <RoomChip label={workshopLabel} dot={workshopColor} soft={softFill(workshopColor)} />}
          {roomChips.map((chip) => (
            <RoomChip key={chip.key} label={chip.label} dot={chip.dot} soft={chip.soft} textColor={chip.textColor} />
          ))}
        </Grid>

        {(placement.type === OrderPlacementType.AREAS || tableCount > 0) && (
          <Grid container alignItems="center" gap={0.5} wrap="nowrap" sx={{ width: 'auto', flexShrink: 0 }}>
            <TableBarOutlinedIcon sx={{ fontSize: ICON_SIZE, color: MUTED_TEXT }} />
            <Typography dir="ltr" sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT }}>
              {tableCount}
            </Typography>
          </Grid>
        )}

        <Grid container alignItems="center" gap={0.5} wrap="nowrap" sx={{ width: 'auto', flexShrink: 0 }}>
          <PeopleAltOutlinedIcon sx={{ fontSize: ICON_SIZE, color: MUTED_TEXT }} />
          <Typography dir="ltr" sx={{ fontSize: FONT_SIZE.secondary, fontWeight: BODY_WEIGHT, whiteSpace: 'nowrap' }}>
            {order.persons}
            {order.checkin && order.checkin.persons !== order.persons
              ? ` (${t('calendar.areas_view.persons_arrived', { count: order.checkin.persons })})`
              : ''}
          </Typography>
        </Grid>

        {order.phone && (
          <Typography
            component="a"
            dir="ltr"
            href={isSmallScreen ? `tel:${order.phone}` : whatsAppLinkFormatter(order.phone, '')}
            target={isSmallScreen ? undefined : '_blank'}
            rel="noreferrer"
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            sx={{
              fontSize: FONT_SIZE.secondary,
              fontWeight: BODY_WEIGHT,
              color: MUTED_TEXT,
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              fontVariantNumeric: 'tabular-nums',
            }}>
            {order.phone}
          </Typography>
        )}

        {(staffComment || customerComment) && (
          <Tooltip
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={4000}
            title={noteTooltip}
            slotProps={{ popper: { onClick: (event: React.MouseEvent) => event.stopPropagation() } }}>
            <span
              onClick={(event) => event.stopPropagation()}
              aria-label={
                staffComment ? t('calendar.areas_view.staff_comment') : t('calendar.areas_view.customer_comment')
              }
              style={{ display: 'inline-flex', flexShrink: 0 }}>
              <NoteAltOutlinedIcon sx={{ fontSize: ICON_SIZE, color: MUTED_TEXT }} />
            </span>
          </Tooltip>
        )}

        {state === AgendaCardState.NO_SHOW && (
          <Typography
            sx={{
              fontSize: FONT_SIZE.detail,
              fontWeight: BODY_WEIGHT,
              color: MUTED_TEXT,
              marginInlineStart: 'auto',
              whiteSpace: 'nowrap',
            }}>
            {t('calendar.areas_view.no_show')}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  return (
    <>
      <Paper
        ref={setDragNodeRef}
        {...dragListeners}
        variant="outlined"
        data-order-id={order.id}
        data-agenda-item-id={order.id}
        sx={{
          ...(isDraggable && { cursor: 'grab' }),
          ...(isDragging && { opacity: 0.4 }),
          borderRadius: CARD_RADIUS,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: CARD_FILL[state],
          ...(state === AgendaCardState.PENDING && { backgroundImage: PENDING_ORDER_HATCH_ON_LIGHT }),
          ...(isPendingHoldExpired(order) && { opacity: 0.6 }),
          ...(isHighlighted && {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
            transition: 'outline-color 300ms',
          }),
        }}>
        <Grid container wrap="nowrap" sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' } }}>
          {rows}
          <Grid
            sx={{
              flexShrink: 0,
              paddingInline: { xs: CARD_PADDING_INLINE.xs, md: 0 },
              paddingInlineEnd: { md: 2.5 },
              paddingBottom: { xs: '12px', md: 0 },
            }}>
            <AgendaOrderActions
              order={order}
              state={state}
              hasExtraItems={extraItems.length > 0}
              amountDue={amountDue}
              canReleaseTable={canReleaseTable}
              formatAmount={formatAmount}
              onCheckin={() => setIsCheckinOpen(true)}
              onReleaseTable={() => setIsReleaseConfirmOpen(true)}
            />
          </Grid>
        </Grid>

        <Collapse in={isExpanded} unmountOnExit>
          <AgendaOrderDetails
            order={order}
            placement={placement}
            extraItems={extraItems}
            itemsTotal={extraItems.reduce((total, line) => total + line.unit_price * line.quantity, 0)}
            paymentLines={order.extra_payment_lines ?? []}
            customerComment={customerComment}
            elapsedText={elapsedText}
            overflowChips={overflowChips}
            projectSelectionPending={isProjectSelectionPending(order, account)}
            timeFormat={timeFormat}
            timezone={account.timezone}
            formatAmount={formatAmount}
          />
        </Collapse>
      </Paper>

      {isCheckinOpen && (
        <CheckinDialog order={order} isOpen={isCheckinOpen} setIsOpen={setIsCheckinOpen} updateItem={updateOrder} />
      )}

      <ConfirmationDialog
        isOpen={isReleaseConfirmOpen}
        onClose={releaseTable}
        title={t('calendar.areas_view.release_table_confirm_title')}
        content={t('calendar.areas_view.release_table_confirm_content', { customerName: name })}
        ctaText={t('calendar.areas_view.release_table')}
        confirmButtonColor="primary"
      />
    </>
  );
};
